'use client'

import { createContext, useContext, useEffect, useState } from 'react'

interface GodModeContextType {
  godModeEnabled: boolean
  setGodModeEnabled: (enabled: boolean) => void
  toggleGodMode: () => void
}

const GodModeContext = createContext<GodModeContextType | undefined>(undefined)

const GOD_MODE_STORAGE_KEY = 'trackz-god-mode-enabled'

export function GodModeProvider({ children }: { children: React.ReactNode }) {
  const [godModeEnabled, setGodModeEnabled] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Load god mode state from localStorage on mount
  useEffect(() => {
    const savedGodMode = localStorage.getItem(GOD_MODE_STORAGE_KEY)
    if (savedGodMode !== null) {
      setGodModeEnabled(savedGodMode === 'true')
    }
    setIsInitialized(true)
  }, [])

  // Save god mode state to localStorage when it changes
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(GOD_MODE_STORAGE_KEY, String(godModeEnabled))
    }
  }, [godModeEnabled, isInitialized])

  const toggleGodMode = () => {
    setGodModeEnabled((prev) => !prev)
  }

  return (
    <GodModeContext.Provider
      value={{ godModeEnabled, setGodModeEnabled, toggleGodMode }}
    >
      {children}
    </GodModeContext.Provider>
  )
}

export function useGodMode() {
  const context = useContext(GodModeContext)
  if (context === undefined) {
    throw new Error('useGodMode must be used within a GodModeProvider')
  }
  return context
}
